/**
 * Audit event contract for the first-class local AuditLog stream.
 *
 * Audit events are body-safe semantic records, not raw agent traces and not
 * debug telemetry. Slice 07 keeps them device-local while preserving enough
 * signer metadata for local verification and later cross-device hardening.
 */
export const AUDIT_EVENT_SCHEMA_VERSION = 'audit-event.v1';
export const SEED_AUDIT_ACTIONS = [
    'audit.key_delegated',
    'settings.route_preference_changed',
    'settings.route_preference_cleared',
    'settings.route_preferences_bulk_changed',
    'settings.privacy_or_disclosure_setting_changed',
    'gdpr.export_requested',
    'gdpr.delete_requested',
    'recovery.started',
    'recovery.succeeded',
    'recovery.failed',
    'llm.route_executed',
    'llm.route_blocked',
    'mission.lifecycle_changed',
    'mission.recommendation_materialized',
    'profile.observation_materialized',
    'source.auth_state_changed',
];
const FORBIDDEN_KEY_RULES = [
    { rule: 'raw prompt field', pattern: /(^|[._-])(raw)?prompts?($|[._-])/i },
    { rule: 'raw completion field', pattern: /(^|[._-])(raw)?completions?($|[._-])/i },
    { rule: 'source body field', pattern: /(source|email|html|raw).*body|body(html|text)/i },
    { rule: 'OAuth token field', pattern: /(oauth|access|refresh)?token/i },
    { rule: 'auth header field', pattern: /auth(orization)?header|authorization/i },
    { rule: 'private key field', pattern: /privatekey|private_key/i },
    { rule: 'provider credential field', pattern: /credential|secret|password|apikey|api_key/i },
    { rule: 'unredacted URL field', pattern: /(^|[._-])(source)?url($|[._-])|href/i },
    { rule: 'content item id field', pattern: /contentitemid|content_item_id|itemid|item_id/i },
    { rule: 'content item hash field', pattern: /contentitemhash|content_item_hash|itemhash|item_hash|contenthash|content_hash/i },
];
const FORBIDDEN_VALUE_RULES = [
    { rule: 'unredacted URL value', pattern: /\bhttps?:\/\//i },
    { rule: 'auth header value', pattern: /\bAuthorization\s*:/i },
    { rule: 'bearer token value', pattern: /\bBearer\s+[A-Za-z0-9._~+/=-]+/i },
];
const BODY_SAFE_STABLE_REF_PATTERN = /^[A-Za-z0-9][A-Za-z0-9._:/#-]{0,159}$/;
const FORBIDDEN_STABLE_REF_PATTERN = /https?:\/\/|content[_-]?item|item[_-]?(id|hash)|content[_-]?hash|source[_-]?hash/i;
export function canonicalizeAuditEventForHash(event) {
    const { eventHash: _eventHash, signature: _signature, ...hashInput } = event;
    return canonicalJSON(hashInput);
}
export function canonicalizeAuditEventForSigning(event) {
    const { signature: _signature, ...signingBody } = event;
    return canonicalJSON(signingBody);
}
export function isBodySafeAuditStableRef(stableRef) {
    return (BODY_SAFE_STABLE_REF_PATTERN.test(stableRef) &&
        !FORBIDDEN_STABLE_REF_PATTERN.test(stableRef));
}
export function findAuditEventPrivacyViolations(value) {
    const violations = [];
    scanPrivacy(value, '$', violations);
    return violations;
}
export function validateAuditEventV1(value) {
    const errors = [];
    const privacyViolations = findAuditEventPrivacyViolations(value);
    if (!isPlainObject(value)) {
        return {
            ok: false,
            errors: ['Audit event must be a plain object'],
            privacyViolations,
        };
    }
    requireLiteral(value, 'schemaVersion', AUDIT_EVENT_SCHEMA_VERSION, errors);
    requireString(value, 'id', errors);
    requireString(value, 'userId', errors);
    requireString(value, 'deviceId', errors);
    requireString(value, 'auditKeyId', errors);
    requireString(value, 'delegationId', errors);
    requireString(value, 'algorithm', errors);
    requireNumber(value, 'ts', errors);
    requireString(value, 'action', errors);
    requireString(value, 'eventHash', errors);
    requireString(value, 'signature', errors);
    if (!isPlainObject(value.actor)) {
        errors.push('actor must be a plain object');
    }
    else {
        const kind = value.actor.kind;
        if (kind !== 'user' && kind !== 'agent' && kind !== 'system') {
            errors.push('actor.kind must be user, agent, or system');
        }
        if (value.actor.id !== undefined && typeof value.actor.id !== 'string') {
            errors.push('actor.id must be a string when present');
        }
    }
    if (!isPlainObject(value.target)) {
        errors.push('target must be a plain object');
    }
    else {
        requireString(value.target, 'kind', errors, 'target.kind');
        if (Object.prototype.hasOwnProperty.call(value.target, 'id')) {
            errors.push('target.id is forbidden; use body-safe target.stableRef');
        }
        if (value.target.stableRef !== undefined) {
            if (typeof value.target.stableRef !== 'string') {
                errors.push('target.stableRef must be a string when present');
            }
            else if (!isBodySafeAuditStableRef(value.target.stableRef)) {
                errors.push('target.stableRef must be a body-safe product reference');
            }
        }
        if (value.target.label !== undefined && typeof value.target.label !== 'string') {
            errors.push('target.label must be a string when present');
        }
    }
    if (value.result !== undefined) {
        const result = value.result;
        if (result !== 'success' && result !== 'failure' && result !== 'blocked' && result !== 'deferred') {
            errors.push('result must be success, failure, blocked, or deferred when present');
        }
    }
    for (const optionalStringKey of ['reason', 'traceId', 'routeId', 'previousEventHash']) {
        if (value[optionalStringKey] !== undefined && typeof value[optionalStringKey] !== 'string') {
            errors.push(`${optionalStringKey} must be a string when present`);
        }
    }
    return {
        ok: errors.length === 0 && privacyViolations.length === 0,
        errors,
        privacyViolations,
    };
}
export function assertAuditEventV1(value) {
    const result = validateAuditEventV1(value);
    if (!result.ok) {
        const details = [
            ...result.errors,
            ...result.privacyViolations.map((v) => `${v.path}: ${v.rule}`),
        ].join('; ');
        throw new Error(`Invalid AuditEventV1: ${details}`);
    }
}
function scanPrivacy(value, path, violations) {
    if (typeof value === 'string') {
        for (const { rule, pattern } of FORBIDDEN_VALUE_RULES) {
            if (pattern.test(value)) {
                violations.push({ path, rule });
            }
        }
        return;
    }
    if (value === null || typeof value !== 'object')
        return;
    if (Array.isArray(value)) {
        value.forEach((item, index) => scanPrivacy(item, `${path}[${index}]`, violations));
        return;
    }
    if (!isPlainObject(value)) {
        violations.push({ path, rule: 'non-plain object' });
        return;
    }
    for (const [key, child] of Object.entries(value)) {
        const childPath = `${path}.${key}`;
        for (const { rule, pattern } of FORBIDDEN_KEY_RULES) {
            if (pattern.test(key)) {
                violations.push({ path: childPath, rule });
            }
        }
        scanPrivacy(child, childPath, violations);
    }
}
function canonicalJSON(value) {
    if (value === null)
        return 'null';
    switch (typeof value) {
        case 'boolean':
            return value ? 'true' : 'false';
        case 'number':
            return encodeNumber(value);
        case 'string':
            return encodeString(value);
        case 'bigint':
            throw new TypeError('canonicalJSON: BigInt is not supported');
        case 'function':
        case 'symbol':
            throw new TypeError(`canonicalJSON: ${typeof value} is not supported`);
        case 'undefined':
            throw new TypeError('canonicalJSON: undefined is not supported');
        case 'object':
            if (Array.isArray(value))
                return encodeArray(value);
            if (!isPlainObject(value)) {
                throw new TypeError('canonicalJSON: only plain objects are supported');
            }
            return encodeObject(value);
    }
    throw new TypeError(`canonicalJSON: unsupported value type ${typeof value}`);
}
function encodeNumber(n) {
    if (!Number.isFinite(n)) {
        throw new TypeError(`canonicalJSON: non-finite number ${n}`);
    }
    if (Object.is(n, -0))
        return '0';
    return String(n);
}
function encodeString(s) {
    let out = '"';
    for (let i = 0; i < s.length; i += 1) {
        const ch = s.charCodeAt(i);
        if (ch === 0x22)
            out += '\\"';
        else if (ch === 0x5c)
            out += '\\\\';
        else if (ch === 0x08)
            out += '\\b';
        else if (ch === 0x09)
            out += '\\t';
        else if (ch === 0x0a)
            out += '\\n';
        else if (ch === 0x0c)
            out += '\\f';
        else if (ch === 0x0d)
            out += '\\r';
        else if (ch < 0x20)
            out += '\\u' + ch.toString(16).padStart(4, '0');
        else
            out += s[i];
    }
    return out + '"';
}
function encodeArray(arr) {
    const parts = [];
    for (const item of arr) {
        parts.push(item === undefined ? 'null' : canonicalJSON(item));
    }
    return '[' + parts.join(',') + ']';
}
function encodeObject(obj) {
    const keys = Object.keys(obj).sort();
    const parts = [];
    for (const key of keys) {
        const value = obj[key];
        if (value === undefined)
            continue;
        parts.push(encodeString(key) + ':' + canonicalJSON(value));
    }
    return '{' + parts.join(',') + '}';
}
function isPlainObject(value) {
    if (value === null || typeof value !== 'object')
        return false;
    const prototype = Object.getPrototypeOf(value);
    return prototype === Object.prototype || prototype === null;
}
function requireString(obj, key, errors, label = key) {
    if (typeof obj[key] !== 'string' || obj[key] === '') {
        errors.push(`${label} must be a non-empty string`);
    }
}
function requireNumber(obj, key, errors) {
    if (typeof obj[key] !== 'number' || !Number.isFinite(obj[key])) {
        errors.push(`${key} must be a finite number`);
    }
}
function requireLiteral(obj, key, literal, errors) {
    if (obj[key] !== literal) {
        errors.push(`${key} must be ${literal}`);
    }
}
